import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, FormikProps } from 'formik'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import {
  Column,
  ColumnBodyOptions,
  ColumnEditorOptions,
} from 'primereact/column'
import {
  DataTable,
  DataTableDataSelectableEvent,
  DataTableEditingRows,
  DataTableRowEditCompleteEvent,
  DataTableRowEditEvent,
  DataTableRowEditSaveEvent,
  DataTableRowEditValidatorOptions,
  DataTableValue,
  DataTableValueArray,
} from 'primereact/datatable'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'

import { CustomFieldDefinition } from '../../../../master-data-management'
import { FormFieldName, FormValues } from './CompanyCodes.form'

type Props = {
  formik: FormikProps<FormValues>
  customFieldDefinitions: CustomFieldDefinition[] | undefined
  isLoading: boolean
  hasLoadingRowId: (rowId: string) => boolean
  isBulkDeleteRunning: boolean
  handleCreate: (customFieldDefinition: CustomFieldDefinition) => Promise<void>
  handleDelete: (
    customFieldDefinitions: CustomFieldDefinition[],
    callback?: () => void,
  ) => Promise<void>
  isFormFieldValid: (index: number, name: FormFieldName) => boolean
  getFormErrorMessage: (
    index: number,
    name: FormFieldName,
  ) => false | JSX.Element
}

export const CompanyCodesView: React.FC<Props> = ({
  formik,
  isLoading,
  hasLoadingRowId,
  isBulkDeleteRunning,
  handleCreate,
  handleDelete,
  isFormFieldValid,
  getFormErrorMessage,
}) => {
  const { t } = useTranslation()

  const [editingRows, setEditingRows] = useState<DataTableEditingRows>({})
  const [selectedBatches, setSelectedBatches] = useState<
    CustomFieldDefinition[]
  >([])

  const isEditingMode = Object.keys(editingRows).length > 0
  const hasNoSelectedRows = selectedBatches.length === 0

  const handleAddNewRow = async () => {
    const newRowIndex = formik.values.customFieldDefinitions?.length || 0
    const newRowData: CustomFieldDefinition = {
      Id: `newProperty-${newRowIndex}`,
      Key: '',
      Datatype: 'String',
      Type: 'CompanyCode',
    }

    await formik.setFieldValue(
      `customFieldDefinitions.${newRowIndex}`,
      newRowData,
    )
    setEditingRows(prev => ({ ...prev, [newRowData.Id]: true }))
  }

  const handleDeleteRows = async (
    customFieldDefinitions: CustomFieldDefinition[],
  ) => {
    if (customFieldDefinitions) {
      await handleDelete(customFieldDefinitions, () => setSelectedBatches([]))
    }
  }

  const onRowEditInit = (event: DataTableRowEditEvent) => {}

  const onRowEditChange = (event: DataTableRowEditEvent) => {
    setEditingRows(event.data)
  }

  const onRowEditCancel = (event: DataTableRowEditEvent) => {
    // If the batch is newly added it has the id "newProperty-X" we need to remove this from formik.
    // Otherwise it was already added but the user wants to cancel the editing and remove the changes.
    const isNewBatch = event.data.Id === `newProperty-${event.index}`
    if (isNewBatch) {
      const updatedBatches = formik.values.customFieldDefinitions?.filter(
        batch => batch.Id !== event.data.Id,
      )
      formik.setFieldValue('customFieldDefinitions', updatedBatches)
    } else {
      const originalBatch =
        formik.initialValues.customFieldDefinitions?.[event.index]
      formik.setFieldValue(
        `customFieldDefinitions.${event.index}`,
        originalBatch,
      )
    }
  }

  const rowEditValidator = (
    data: DataTableValue,
    options: DataTableRowEditValidatorOptions<DataTableValueArray>,
  ) => {
    const rowErrors =
      Array.isArray(formik.errors) &&
      (formik.errors[options.rowIndex] as FormikErrors<CustomFieldDefinition>)
    return !(rowErrors && Object.keys(rowErrors).length > 0)
  }

  const onRowEditSave = (
    event: DataTableRowEditSaveEvent<DataTableValueArray>,
  ) => {
    formik.handleSubmit()
  }

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    const { newData, index } = event

    formik.setFieldValue(
      `customFieldDefinitions.${index}.Key`,
      newData.Key as CustomFieldDefinition,
    )
    formik.setFieldValue(
      `customFieldDefinitions.${index}.DataType`,
      newData.DataType as CustomFieldDefinition,
    )
    formik.validateForm()

    const isNewBatch = newData.Id === `newProperty-${event.index}`

    // if (isNewBatch) {
    handleCreate(newData as CustomFieldDefinition)
    // }

    setEditingRows({})
  }

  const isDataSelectable = (
    event: DataTableDataSelectableEvent,
  ): boolean | null => {
    const isNewItem = event.data.Id === `newProperty-${event.index}`
    return !isNewItem
  }

  const header = (
    <div className="flex justify-content-between align-items-center gap-3 flex-wrap">
      <h3>{t('buyerSettings.additionalProperties.companyCode.table.title')}</h3>

      <div className="flex flex-shrink-0 gap-3">
        <Button
          label={t(
            'buyerSettings.additionalProperties.companyCode.table.delete.label',
          )}
          onClick={() => handleDeleteRows(selectedBatches)}
          icon="pi pi-trash"
          outlined
          disabled={hasNoSelectedRows || isEditingMode}
          loading={isBulkDeleteRunning}
        />
        <Button
          label={t(
            'buyerSettings.additionalProperties.companyCode.table.add.label',
          )}
          onClick={handleAddNewRow}
          icon="pi pi-plus"
          outlined
          disabled={isEditingMode}
        />
      </div>
    </div>
  )

  const emptyMessage = (
    <div className="text-center">
      <p className="font-bold text-base">
        {t(
          'buyerSettings.additionalProperties.companyCode.table.emptyMessageTitle',
        )}
      </p>
      <p className="text-sm">
        {t(
          'buyerSettings.additionalProperties.companyCode.table.emptyMessageDetails',
        )}
      </p>
    </div>
  )

  const keyEditor = (options: ColumnEditorOptions) => {
    return (
      <>
        <InputText
          id="Key"
          type="text"
          autoFocus
          className="w-full"
          value={formik.values.customFieldDefinitions?.[options.rowIndex].Key} // {options.value}
          onChange={event => {
            const value = event.target.value || ''
            options.editorCallback!(value)

            formik.setFieldValue(
              `customFieldDefinitions.${options.rowIndex}.Key`,
              value,
            )
          }} // {(e: React.ChangeEvent<HTMLInputElement>) => options.editorCallback!(e.target.value)}
        />
        <div>{getFormErrorMessage(options.rowIndex, 'Key')}</div>
      </>
    )
  }

  const datatypeEditor = (options: ColumnEditorOptions) => {
    const datatypes: CustomFieldDefinition['Datatype'][] = [
      'String',
      'Number',
      'Boolean',
      'DateTime',
    ]

    return (
      <>
        <Dropdown
          value={
            formik.values.customFieldDefinitions?.[options.rowIndex].Datatype
          }
          onChange={event => {
            const value = event.target.value || ''
            options.editorCallback!(value)

            formik.setFieldValue(
              `customFieldDefinitions.${options.rowIndex}.Datatype`,
              value,
            )
          }}
          options={datatypes}
          placeholder="Select a City"
          className="w-full"
        />

        <div>{getFormErrorMessage(options.rowIndex, 'Datatype')}</div>
      </>
    )
  }

  const actionBodyTemplate = (
    rowData: CustomFieldDefinition,
    options: ColumnBodyOptions,
  ) => {
    if (options.rowEditor?.editing)
      return (
        <div className="flex gap-2">
          <Button
            icon="pi pi-times"
            aria-label={t(
              'buyerSettings.additionalProperties.companyCode.table.cancel.label',
            )}
            className="p-button-rounded p-button-text"
            onClick={event => {
              if (options.rowEditor?.onCancelClick) {
                options.rowEditor.onCancelClick(event)
              }
            }}
          />

          <Button
            icon="pi pi-check"
            aria-label={t(
              'buyerSettings.additionalProperties.companyCode.table.submit.label',
            )}
            className="p-button-rounded p-button-text"
            onClick={event => {
              if (options.rowEditor?.onSaveClick) {
                options.rowEditor.onSaveClick(event)
              }
            }}
            type="submit"
          />
        </div>
      )

    return (
      <Button
        icon="pi pi-trash"
        aria-label={t(
          'buyerSettings.additionalProperties.companyCode.table.delete.label',
        )}
        className="p-button-rounded p-button-text"
        severity="danger"
        onClick={async () => {
          await handleDelete([rowData], () =>
            setSelectedBatches(prevSelectedBatches =>
              prevSelectedBatches?.filter(batch => batch.Id !== rowData.Id),
            ),
          )
        }}
        loading={hasLoadingRowId(rowData.Id)}
        disabled={isEditingMode}
      />
    )
  }

  return (
    <>
      <div className="col-12 lg:col-4">
        <h3 className="m-0">
          {t('buyerSettings.additionalProperties.companyCode.title')}
        </h3>
        <p>{t('buyerSettings.additionalProperties.companyCode.description')}</p>
      </div>
      <div className="col-12 lg:col-7 lg:col-offset-1">
        <DataTable
          value={formik.values.customFieldDefinitions}
          header={header}
          stateKey=""
          selectionMode="checkbox"
          isDataSelectable={isDataSelectable}
          selection={selectedBatches}
          onSelectionChange={e =>
            setSelectedBatches(e.value as CustomFieldDefinition[])
          }
          dataKey="Id"
          editMode="row"
          editingRows={editingRows}
          // Callbacks
          onRowEditInit={onRowEditInit}
          onRowEditChange={onRowEditChange}
          onRowEditCancel={onRowEditCancel}
          rowEditValidator={rowEditValidator}
          onRowEditSave={onRowEditSave}
          onRowEditComplete={onRowEditComplete}
          className="mt-4"
          emptyMessage={emptyMessage}
          loading={isLoading}
          showGridlines
        >
          <Column
            selectionMode="multiple"
            align="center"
            className="w-1"
          ></Column>
          <Column
            key="Key"
            field="Key"
            header={t(
              'buyerSettings.additionalProperties.companyCode.table.columns.key.header',
            )}
            editor={options => keyEditor(options)}
            className="w-6"
          ></Column>
          <Column
            key="Datatype"
            field="Datatype"
            header={t(
              'buyerSettings.additionalProperties.companyCode.table.columns.datatype.header',
            )}
            editor={options => datatypeEditor(options)}
            className="w-6"
          ></Column>
          <Column
            rowEditor
            key="buttons"
            body={(e, options) => actionBodyTemplate(e, options)}
            align="center"
            className="w-2"
          ></Column>
        </DataTable>
      </div>
    </>
  )
}
